import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Popover, SegmentedControl, PillList,
} from '../ui';
import MachineFilter from '../InventoryDashboard/MachineFilter';
import SignalsRow from './SignalsRow';
import { CaretIcon, FilterIcon, SearchIcon } from './icons';
import { verdictLabel } from '../../utils/formatting';
import {
    MACHINE_PILL_FIELDS, buildFilterPills, clearFilterField, formatLocations,
    locationList, secondaryFilterCount, toLocationValue,
} from '../../utils/inventoryView';
import { useDealershipSettings } from '../../contexts/settingsContext';

const TYPE_OPTIONS = [{ value: 'All', label: 'All' }, { value: 'N', label: 'New' }, { value: 'U', label: 'Used' }];
const SELL_SPEED_OPTIONS = ['All', 'Quick Sell', 'Normal', 'Slow', 'Critical'];
const BUYER_HEAT_OPTIONS = ['All', 'Hot', 'Warm', 'Cold', 'No Interest'];

const SIGNALS_PILL = 'signals';

const uniqueSorted = (units, key) => [...new Set(units.map((u) => u[key]).filter(Boolean))].sort();

/** D14: Location as a multi-select (one branch keeps today's single value). */
function LocationFilter({ filters, options, onChange }) {
    const selected = locationList(filters.location);
    const label = selected.length === 0 ? 'All Locations' : formatLocations(filters.location);
    const toggle = (name) => {
        const next = selected.includes(name) ? selected.filter((n) => n !== name) : [...selected, name];
        onChange({ ...filters, location: toLocationValue(next) });
    };
    return (
        <Popover
            triggerLabel="Location"
            title="Location"
            width={260}
            triggerClassName={`v2-btn inv2-selbtn${selected.length ? ' is-set' : ''}`}
            triggerContent={<><span className="v2-sr">Location: </span>{label}<CaretIcon /></>}
        >
            <div className="inv2-pop-head">
                <h3 className="v2-pop-title">Location</h3>
                {selected.length > 0 && (
                    <button type="button" className="v2-clear" onClick={() => onChange({ ...filters, location: 'All' })}>
                        Clear location
                    </button>
                )}
            </div>
            <div className="inv2-checks inv2-checks--one" role="group" aria-label="Locations">
                {options.map((name) => (
                    <label key={name}>
                        <input type="checkbox" checked={selected.includes(name)} onChange={() => toggle(name)} />
                        {name}
                    </label>
                ))}
            </div>
        </Popover>
    );
}

LocationFilter.propTypes = {
    filters: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    options: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
};

/** The secondary filters (today's "Filters" row) as a popover. */
function FiltersPopover({ filters, salesmen, secondaryCount, onChange }) {
    const [open, setOpen] = useState(false);
    const { aging: agingSettings } = useDealershipSettings();
    const aging = filters.agingBucket || [];
    const set = (field, value) => onChange({ ...filters, [field]: value });
    const toggleAging = (bucket) => set('agingBucket', aging.includes(bucket) ? aging.filter((b) => b !== bucket) : [...aging, bucket]);
    return (
        <Popover
            triggerLabel="Filters"
            title="Filters"
            width={320}
            open={open}
            onOpenChange={setOpen}
            triggerClassName={`v2-btn${open || secondaryCount > 0 ? ' has-filters' : ''}`}
            triggerContent={<><FilterIcon />Filters</>}
            // Like today's badge: shown while the panel is closed.
            badge={!open && secondaryCount > 0 ? secondaryCount : null}
        >
            <fieldset className="inv2-fld">
                <legend>Aging</legend>
                <div className="inv2-checks">
                    {agingSettings.buckets.map((bucket) => (
                        <label key={bucket.id}>
                            <input type="checkbox" checked={aging.includes(bucket.id)} onChange={() => toggleAging(bucket.id)} />
                            {bucket.label}
                        </label>
                    ))}
                </div>
                {aging.length > 0 && (
                    <button type="button" className="v2-clear inv2-fld-clear" onClick={() => set('agingBucket', [])}>
                        Clear aging
                    </button>
                )}
            </fieldset>
            <label className="v2-fld">
                <span>Sell Speed</span>
                <select className="v2-sel" value={filters.sellSpeed || 'All'} onChange={(e) => set('sellSpeed', e.target.value)}>
                    {SELL_SPEED_OPTIONS.map((o) => <option key={o} value={o}>{o === 'All' ? 'All Speeds' : o}</option>)}
                </select>
            </label>
            <label className="v2-fld">
                <span>Interest</span>
                <select className="v2-sel" value={filters.buyerHeat || 'All'} onChange={(e) => set('buyerHeat', e.target.value)}>
                    {BUYER_HEAT_OPTIONS.map((o) => <option key={o} value={o}>{o === 'All' ? 'All Interest' : o}</option>)}
                </select>
            </label>
            <label className="v2-fld">
                <span>Salesman</span>
                <select className="v2-sel" value={filters.salesman} onChange={(e) => set('salesman', e.target.value)}>
                    <option value="All">All Salesmen</option>
                    {salesmen.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            </label>
        </Popover>
    );
}

FiltersPopover.propTypes = {
    filters: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    salesmen: PropTypes.arrayOf(PropTypes.string).isRequired,
    secondaryCount: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
};

/**
 * "Include committed units (N)": appends the committed units (sale pending,
 * pre-sold, sold) to the grid. Off by default, never saved (?committed=1).
 * N is known once they have been loaded; while they load (20 s and more on
 * a cold cache) the switch is busy: a spinner, "(loading…)" and aria-busy
 * (QA E3).
 */
function CommittedToggle({ page }) {
    const { committedOn, toggleCommitted, committedTotal } = page;
    if (!toggleCommitted) return null;
    const busy = Boolean(committedOn && page.committedLoading);
    let suffix = '';
    if (busy) suffix = ' (loading…)';
    else if (committedTotal != null) suffix = ` (${committedTotal.toLocaleString('en-US')})`;
    return (
        <label className={`inv2-switch${committedOn ? ' is-on' : ''}${busy ? ' is-busy' : ''}`} title="Units marked sold but not yet closed out. They are listed in the table only and never counted in the figures above.">
            <input type="checkbox" role="switch" checked={Boolean(committedOn)} onChange={toggleCommitted} aria-busy={busy || undefined} />
            <span className="inv2-switch-track" aria-hidden="true"><i /></span>
            {`Include committed units${suffix}`}
            {busy && <span className="inv2-switch-spin" aria-hidden="true" />}
        </label>
    );
}

/** What the committed toggle's live region announces (loading, then the rows shown). */
const committedStatusText = (page, committedShown) => {
    if (!page.toggleCommitted || !page.committedOn) return '';
    if (page.committedLoading) return 'Loading committed units…';
    if (page.committedTotal == null) return '';
    return `${committedShown.toLocaleString('en-US')} committed unit${committedShown === 1 ? '' : 's'} shown.`;
};

CommittedToggle.propTypes = { page: PropTypes.object.isRequired }; // eslint-disable-line react/forbid-prop-types

/**
 * The single control card above the table: view title and result count with
 * the global clear and the Columns button; search, type, location, category,
 * Machine, Filters and the committed-units toggle; the Signals /
 * Opportunities row; the active filter pills.
 */
function ControlCard({
    page, allData, shownCount, resultCountRef, machineButtonRef, currentYear, committedShown = 0, committedBreakdownText = '', columnsControl = null,
}) {
    const {
        filters, changeFilters, machineCatalog, machineFacetData, hasActiveFilters, onClearAll,
    } = page;

    const locations = useMemo(() => uniqueSorted(allData, 'BranchName'), [allData]);
    const categories = useMemo(() => uniqueSorted(allData, 'CATEGORY'), [allData]);
    const salesmen = useMemo(() => uniqueSorted(allData, 'Salesman'), [allData]);

    const { aging } = useDealershipSettings();
    const pills = buildFilterPills(filters, machineCatalog, aging.buckets);
    const secondaryCount = secondaryFilterCount(pills);
    // v2 adds a pill for the anomaly selection (QA B6), so a selection made
    // from Needs attention or a link is visible; × clears the signals only.
    const { anomalyFilter, handleAnomalyClearSignals } = page;
    const signalLabels = [...anomalyFilter].map(verdictLabel);
    const pillItems = [
        ...pills.map((p) => ({ id: p.field, label: p.label, value: p.value })),
        ...(signalLabels.length ? [{
            id: SIGNALS_PILL,
            label: 'Signals',
            value: signalLabels.length > 3 ? `${signalLabels.length.toLocaleString('en-US')} signals` : signalLabels.join(', '),
        }] : []),
    ];
    const removePill = (field) => {
        if (field === SIGNALS_PILL) handleAnomalyClearSignals();
        else changeFilters(clearFilterField(filters, field));
    };
    const set = (field, value) => changeFilters({ ...filters, [field]: value });

    return (
        <div className="inv2-ctl">
            <div className="inv2-row">
                <h2 className="inv2-view">All inventory</h2>
                <p className="inv2-count" ref={resultCountRef} tabIndex={-1} data-testid="result-count">
                    {committedShown > 0 ? (
                        <>
                            {shownCount === allData.length
                                ? <b>{shownCount.toLocaleString('en-US')}</b>
                                : <><b>{shownCount.toLocaleString('en-US')}</b> of {allData.length.toLocaleString('en-US')}</>}
                            {' in stock · '}
                            <span className="inv2-count-extra">
                                {`${committedShown.toLocaleString('en-US')} committed${committedBreakdownText ? ` (${committedBreakdownText})` : ''}`}
                            </span>
                        </>
                    ) : (
                        <>
                            <b>{shownCount.toLocaleString('en-US')}</b> of {allData.length.toLocaleString('en-US')} units
                            {page.committedOn && page.committedLoading && <span className="inv2-count-loading">{' · loading committed units…'}</span>}
                        </>
                    )}
                </p>
                <p className="v2-sr" role="status" aria-live="polite" data-testid="committed-status">
                    {committedStatusText(page, committedShown)}
                </p>
                {committedShown > 0 && (
                    <p className="inv2-count-note">Committed units are listed in the table but not counted in the figures above.</p>
                )}
                {hasActiveFilters && (
                    <button
                        type="button"
                        className="v2-clear"
                        onClick={onClearAll}
                        title="Clear all filters, anomaly signals and Open Tasks"
                    >
                        Clear all filters
                    </button>
                )}
                {columnsControl && (
                    <>
                        <span className="inv2-spacer" />
                        {columnsControl}
                    </>
                )}
            </div>

            <div className="inv2-row">
                <label className="v2-search inv2-search">
                    <span className="v2-sr">Search</span>
                    <SearchIcon />
                    <input
                        type="search"
                        placeholder="Search stock #, make, model..."
                        value={filters.search}
                        onChange={(e) => set('search', e.target.value)}
                        autoComplete="off"
                    />
                </label>
                <SegmentedControl
                    label="New or Used filter"
                    options={TYPE_OPTIONS}
                    value={filters.newUsed}
                    onChange={(v) => set('newUsed', v)}
                />
                <LocationFilter filters={filters} options={locations} onChange={changeFilters} />
                <select
                    className={`v2-sel inv2-sel${filters.category !== 'All' ? ' is-set' : ''}`}
                    aria-label="Category"
                    value={filters.category}
                    onChange={(e) => set('category', e.target.value)}
                >
                    <option value="All">All Categories</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="inv2-machine">
                    <MachineFilter
                        filters={filters}
                        onChange={changeFilters}
                        catalog={machineCatalog}
                        facetData={machineFacetData || allData}
                        currentYear={currentYear}
                        resultCount={shownCount}
                        buttonRef={machineButtonRef}
                        buttonClassName="v2-btn inv2-machine-btn"
                        badgeClassName="v2-badge"
                    />
                </div>
                <FiltersPopover filters={filters} salesmen={salesmen} secondaryCount={secondaryCount} onChange={changeFilters} />
                <CommittedToggle page={page} />
            </div>

            <SignalsRow page={page} allData={allData} shownCount={shownCount} />

            <PillList
                label="Active filters"
                items={pillItems}
                onRemove={removePill}
                getFallbackFocus={(field) => (MACHINE_PILL_FIELDS.includes(field) ? machineButtonRef : resultCountRef)}
            />
        </div>
    );
}

ControlCard.propTypes = {
    page: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    allData: PropTypes.arrayOf(PropTypes.object).isRequired,
    shownCount: PropTypes.number.isRequired,
    resultCountRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
    machineButtonRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
    currentYear: PropTypes.number.isRequired,
    committedShown: PropTypes.number,
    committedBreakdownText: PropTypes.string,
    columnsControl: PropTypes.node,
};

export default ControlCard;
